import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { MeResponse } from '@intelimove/contracts';
import { api, ApiError, setAccessChangedHandler, setUnauthenticatedHandler } from '../api/client';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthValue {
  status: Status;
  me: MeResponse | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  switchOrganization(id: string): Promise<void>;
  /** Sessão nova (cadastro) ou tenant novo (empresa criada): substitui todo o contexto. */
  replaceMe(me: MeResponse): void;
}

const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    setUnauthenticatedHandler(() => { setMe(null); setStatus('anonymous'); });
    // Relê a sessão no máximo a cada 5 s quando o servidor recusa por mudança de acesso (IM-SEC-119).
    let last = 0;
    setAccessChangedHandler(() => {
      if (Date.now() - last < 5000) return;
      last = Date.now();
      api<MeResponse>('/api/v1/me').then((m) => setMe((prev) => (JSON.stringify(prev) === JSON.stringify(m) ? prev : m))).catch(() => undefined);
    });
    api<MeResponse>('/api/v1/me')
      .then((m) => { setMe(m); setStatus('authenticated'); })
      .catch((e) => { if (!(e instanceof ApiError) || e.unauthenticated) setStatus('anonymous'); else setStatus('anonymous'); });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const m = await api<MeResponse>('/api/v1/auth/login', { method: 'POST', body: { email, password } });
    setMe(m);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await api('/api/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
    setMe(null);
    setStatus('anonymous');
  }, []);

  const switchOrganization = useCallback(async (organizationId: string) => {
    const m = await api<MeResponse>('/api/v1/me/active-organization', { method: 'POST', body: { organizationId } });
    setMe(m);
  }, []);

  const replaceMe = useCallback((m: MeResponse) => { setMe(m); setStatus('authenticated'); }, []);
  const value = useMemo(() => ({ status, me, login, logout, switchOrganization, replaceMe }), [status, me, login, logout, switchOrganization, replaceMe]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider ausente');
  return v;
}

export const ROLE_LABEL = { OWNER: 'Proprietário', ADMIN: 'Administrador', MEMBER: 'Membro' } as const;
